import React, { useMemo, useState } from 'react';
import { Calculator as CalculatorIcon, FileDown, Printer, Table } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const initialInputs = {
  loanAmount: '300000',
  apr: '5',
  payments: '360',
  extraPayment: '0',
};

const parseNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value, decimals = 2) => {
  const safeValue = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(safeValue);
};

const createSchedule = (inputs) => {
  const loanAmount = parseNumber(inputs.loanAmount);
  const apr = parseNumber(inputs.apr) / 100;
  const periods = Math.max(Math.floor(parseNumber(inputs.payments)), 0);
  const extra = Math.max(parseNumber(inputs.extraPayment), 0);

  if (loanAmount <= 0 || periods <= 0 || apr < 0) {
    return {
      schedule: [],
      totalInterest: 0,
      totalPaid: 0,
      payoffPayment: 0,
    };
  }

  const monthlyRate = apr / 12;
  const basePayment = monthlyRate > 0
    ? loanAmount * ((monthlyRate * Math.pow(1 + monthlyRate, periods)) / (Math.pow(1 + monthlyRate, periods) - 1))
    : loanAmount / periods;

  const schedule = [];
  let balance = loanAmount;
  let totalInterest = 0;
  let paymentIndex = 1;

  while (balance > 0 && paymentIndex <= periods + 1) {
    const interest = monthlyRate * balance;
    const principal = Math.min(balance, basePayment - interest);
    let totalPayment = principal + interest;

    let appliedExtra = 0;
    if (extra > 0 && balance - principal > 0) {
      appliedExtra = Math.min(extra, balance - principal);
      totalPayment += appliedExtra;
    }

    totalInterest += interest;
    const newBalance = Math.max(balance - principal - appliedExtra, 0);

    schedule.push({
      payment: paymentIndex,
      paymentAmount: totalPayment,
      basePayment,
      extraPayment: appliedExtra,
      principalPaid: principal + appliedExtra,
      interestPaid: interest,
      remainingBalance: newBalance,
    });

    balance = newBalance;
    if (newBalance <= 0) {
      break;
    }
    paymentIndex += 1;
  }

  const totalPaid = schedule.reduce((sum, row) => sum + row.paymentAmount, 0);

  return {
    schedule,
    totalInterest,
    totalPaid,
    basePayment,
  };
};

const exportCSV = (schedule, t) => {
  if (schedule.length === 0) {
    return;
  }

  const headers = [
    t('loanAmortization.table.payment'),
    t('loanAmortization.table.basePayment'),
    t('loanAmortization.table.extraPayment'),
    t('loanAmortization.table.principal'),
    t('loanAmortization.table.interest'),
    t('loanAmortization.table.balance'),
  ];

  const rows = schedule.map((row) => ([
    row.payment,
    row.basePayment.toFixed(2),
    row.extraPayment.toFixed(2),
    (row.principalPaid - row.extraPayment).toFixed(2),
    row.interestPaid.toFixed(2),
    row.remainingBalance.toFixed(2),
  ]));

  const csvContent = [headers, ...rows]
    .map((columns) => columns.map((value) => `"${value}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'loan_amortization_schedule.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const printSchedule = () => {
  window.print();
};

const LoanAmortizationCalculator = () => {
  const [inputs, setInputs] = useState(initialInputs);
  const { t } = useTranslation();

  const results = useMemo(() => createSchedule(inputs), [inputs]);

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CalculatorIcon className="h-5 w-5 text-primary-600" />
          {t('loanAmortization.title', 'Loan Amortization Schedule Calculator')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('loanAmortization.description', 'Enter your loan details, see each payment until payoff, and export or print the full amortization schedule.')}
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <CalculatorIcon className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('loanAmortization.section.inputs')}</h3>
          </div>
          <div className="space-y-4">
            <CalculatorInput
              label={t('loanAmortization.input.amount')}
              value={inputs.loanAmount}
              onChange={(value) => handleInputChange('loanAmount', value)}
              prefix="$"
            />
            <CalculatorInput
              label={t('loanAmortization.input.apr')}
              value={inputs.apr}
              onChange={(value) => handleInputChange('apr', value)}
              suffix="%"
            />
            <CalculatorInput
              label={t('loanAmortization.input.payments')}
              value={inputs.payments}
              onChange={(value) => handleInputChange('payments', value)}
            />
            <CalculatorInput
              label={t('loanAmortization.input.extra')}
              value={inputs.extraPayment}
              onChange={(value) => handleInputChange('extraPayment', value)}
              prefix="$"
            />
          </div>
          <div className="bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">{t('loanAmortization.output.basePayment')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.basePayment)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">{t('loanAmortization.output.totalInterest')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.totalInterest)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">{t('loanAmortization.output.totalPaid')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(results.totalPaid)}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Table className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('loanAmortization.section.schedule')}</h3>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => exportCSV(results.schedule, t)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-dark-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                <FileDown className="h-4 w-4" />
                {t('loanAmortization.actions.export')}
              </button>
              <button
                type="button"
                onClick={printSchedule}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-dark-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                <Printer className="h-4 w-4" />
                {t('loanAmortization.actions.print')}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700 text-sm">
              <thead className="bg-gray-50 dark:bg-dark-800">
                <tr className="text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                  <th className="px-3 py-2 text-left">{t('loanAmortization.table.payment')}</th>
                  <th className="px-3 py-2 text-right">{t('loanAmortization.table.basePayment')}</th>
                  <th className="px-3 py-2 text-right">{t('loanAmortization.table.extraPayment')}</th>
                  <th className="px-3 py-2 text-right">{t('loanAmortization.table.principal')}</th>
                  <th className="px-3 py-2 text-right">{t('loanAmortization.table.interest')}</th>
                  <th className="px-3 py-2 text-right">{t('loanAmortization.table.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {results.schedule.map((row, index) => (
                  <tr key={row.payment} className={index % 2 === 0 ? 'bg-white dark:bg-dark-900' : 'bg-gray-50 dark:bg-dark-800'}>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{row.payment}</td>
                    <td className="px-3 py-2 text-right text-gray-900 dark:text-white">{formatCurrency(row.basePayment)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">{formatCurrency(row.extraPayment)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">{formatCurrency(row.principalPaid - row.extraPayment)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-200">{formatCurrency(row.interestPaid)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-900 dark:text-white">{formatCurrency(row.remainingBalance)}</td>
                  </tr>
                ))}
                {results.schedule.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                      {t('loanAmortization.empty', 'Enter loan inputs to generate the schedule.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoanAmortizationCalculator;
